import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { dateInput } from 'components/final-form/DateInput';
import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import { editContractDocument, saveContractDocument } from 'services/contractDocumentService';
import { ContractDocumentType } from 'ts-types/api.enums';
import { UpsertContractDocumentDto } from 'ts-types/api.types';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  contractDocumentId?: number,
  contractDocument?: UpsertContractDocumentDto
}

interface State {
  errorMessage?: string,
  contractDocumentTypes: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class UpsertContractDocumentForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {getMessage} = this.props;
    const contractDocumentTypeKeys = Object.values(ContractDocumentType);

    //const contractDocumentTypes: Array<DropdownOption> = Object.values(ContractDocumentType).map((value: string) => {
    //  return {
    //    key: value,
    //    text: value,
    //    value: value
    //  }
    //});
    const contractDocumentTypes = stringArrayToDropdownOptionArray(
        contractDocumentTypeKeys,
        getMessage,
        "contractDocumentType",
        contractDocumentTypeKeys
    );

    this.state = {
      contractDocumentTypes: contractDocumentTypes,
      cancelTokenSource
    }

  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialContractDocumentValues = (): Partial<UpsertContractDocumentDto> => {
    return {};
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage('error.general'))
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<UpsertContractDocumentDto>) => {
    const {contractDocumentId, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    if (contractDocumentId) {
      await editContractDocument(contractDocumentId, values, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await saveContractDocument(values, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  render(): React.ReactNode {

    const {contractDocument = this.initialContractDocumentValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={contractDocument}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({form, handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection(form)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderBasicInformationSection = (form: FormApi): React.ReactNode => {

    const {getMessage} = this.props;
    const {contractDocumentTypes} = this.state;

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      documentType: contractDocumentTypes
    };

    const basicInfoFields = [
      selectInput({
            name: "documentType",
            validate: validator
          },
          selectMap
      ),
      dateInput({
        name: 'validFromDate',
        validate: required
      }),
      textInput({
        name: "documentVersion",
        validate: validator
      }),
      textInput({
        name: "descriptionDe",
        validate: validator
      }),
      textInput({
        name: "documentUrlDe",
        validate: validator
      }),
      textInput({
        name: "descriptionFr",
        validate: validator
      }),
      textInput({
        name: "documentUrlFr",
        validate: validator
      }),
      textInput({
        name: "descriptionIt",
        validate: validator
      }),
      textInput({
        name: "documentUrlIt",
        validate: validator
      }),
      textInput({
        name: "descriptionEn",
        validate: validator
      }),
      textInput({
        name: "documentUrlEn",
        validate: validator
      })
    ];


    const mappedLeftRow = basicInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage(`contractDocument.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={16} verticalAlign="middle" textAlign="left">
              <Header as='h5'>{getMessage("contractDocument.basicInformation")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

}

export default withAuthContext(withMessages(UpsertContractDocumentForm));

